import { useRef, useEffect, useState, useCallback } from "react";
import { Octave } from "../octave/octave";
import { setPianoPointerDown } from "../../../events/piano-drag";
import "./piano-roll.css";

export const PianoRoll = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const keyContainerRef = useRef<HTMLDivElement>(null);
  const [thumbRatio, setThumbRatio] = useState(0.15);
  const [scrollRatio, setScrollRatio] = useState(0.5);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScrollRatio = useRef(0);

  const updateThumbRatio = useCallback(() => {
    const kc = keyContainerRef.current;
    if (!kc) return;
    setThumbRatio(Math.min(kc.clientWidth / kc.scrollWidth, 1));
  }, []);

  // Sync thumb position when key container is scrolled directly (touch, etc.)
  useEffect(() => {
    const kc = keyContainerRef.current;
    if (!kc) return;

    const onScroll = () => {
      if (isDragging.current) return;
      const max = kc.scrollWidth - kc.clientWidth;
      if (max > 0) setScrollRatio(kc.scrollLeft / max);
    };

    const onGlobalPointerUp = () => setPianoPointerDown(false);

    kc.addEventListener("scroll", onScroll);
    updateThumbRatio();
    window.addEventListener("resize", updateThumbRatio);
    window.addEventListener("pointerup", onGlobalPointerUp);

    return () => {
      kc.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateThumbRatio);
      window.removeEventListener("pointerup", onGlobalPointerUp);
    };
  }, [updateThumbRatio]);

  // Apply normalized scroll position to key container
  useEffect(() => {
    const kc = keyContainerRef.current;
    if (!kc) return;
    const max = kc.scrollWidth - kc.clientWidth;
    kc.scrollLeft = scrollRatio * max;
  }, [scrollRatio]);

  const onThumbPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      isDragging.current = true;
      dragStartX.current = e.clientX;
      dragStartScrollRatio.current = scrollRatio;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [scrollRatio]
  );

  const onThumbPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging.current) return;
      const track = trackRef.current;
      if (!track) return;
      const tw = track.clientWidth;
      const maxTravel = tw - tw * thumbRatio;
      if (maxTravel <= 0) return;
      const dx = e.clientX - dragStartX.current;
      setScrollRatio(
        Math.max(
          0,
          Math.min(1, dragStartScrollRatio.current + dx / maxTravel)
        )
      );
    },
    [thumbRatio]
  );

  const onThumbPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Click on track background to jump scroll position
  const onTrackClick = useCallback(
    (e: React.MouseEvent) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const tw = track.clientWidth;
      const thumbW = tw * thumbRatio;
      const maxTravel = tw - thumbW;
      if (maxTravel <= 0) return;
      setScrollRatio(
        Math.max(0, Math.min(1, (clickX - thumbW / 2) / maxTravel))
      );
    },
    [thumbRatio]
  );

  // Mouse wheel on scrollbar area scrolls the keys
  const onTrackWheel = useCallback((e: React.WheelEvent) => {
    const kc = keyContainerRef.current;
    if (!kc) return;
    kc.scrollLeft += e.deltaX || e.deltaY;
  }, []);

  const thumbLeft = `calc(3px + ${scrollRatio * (100 - thumbRatio * 100)}%)`;
  const thumbWidth = `calc(${Math.max(thumbRatio * 100, 5)}% - 6px)`;

  return (
    <div className="sk-piano-roll">
      <div className="sk-piano-scrollbar-wrapper">
        <div
          className="sk-piano-scrollbar-track"
          ref={trackRef}
          onClick={onTrackClick}
          onWheel={onTrackWheel}
        >
          <div
            className="sk-piano-scrollbar-thumb"
            style={{ left: thumbLeft, width: thumbWidth }}
            onPointerDown={onThumbPointerDown}
            onPointerMove={onThumbPointerMove}
            onPointerUp={onThumbPointerUp}
            onPointerCancel={onThumbPointerUp}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
      <div className="sk-key-container" ref={keyContainerRef}>
        <Octave octaveNumber={0} />
        <Octave octaveNumber={1} />
        <Octave octaveNumber={2} />
        <Octave octaveNumber={3} />
        <Octave octaveNumber={4} />
        <Octave octaveNumber={5} />
        <Octave octaveNumber={6} />
        <Octave octaveNumber={7} />
      </div>
    </div>
  );
};
